import React, { forwardRef, useEffect, useState } from 'react';
import { Image, LayoutChangeEvent, View } from 'react-native';

import { Theme } from '@constants';
import { useLoadingManager, useMercatorMap } from '@hooks';

import { styles } from './styles';

import { replaceTemplateValues } from '../../../helpers/StringUtils';
import { MapTileSizeMethod } from '../../../hooks/useMercatorMap';

export const MapTileLayer = forwardRef<View, MapTileLayerProps>(
  (
    {
      center,
      zoom,
      tileDimension = 512,
      uriTemplate,
      onTilesLoaded,
      alpha = 1,
      tileSizeMethod = MapTileSizeMethod.none,
    },
    reference,
  ) => {
    // This is the size of the rendered map component.  We use this while calculating map zooms.
    const [mapSize, setMapSize] = useState<MapSize | null>(null);

    const mercatorMap = useMercatorMap({
      center,
      zoom,
      tileDimension,
      viewWidth: (mapSize?.width ?? 1) * Theme.pixelRatio(),
      viewHeight: (mapSize?.height ?? 1) * Theme.pixelRatio(),
      tileSizeMethod,
    });

    const loadingManager = useLoadingManager((newIsLoading: boolean) => {
      if (onTilesLoaded !== undefined) {
        onTilesLoaded(newIsLoading ? 1 : 0);
      }
    });

    useEffect(() => {
      loadingManager.reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [center, zoom, uriTemplate]);

    let content = null;

    if (mapSize?.width && mapSize?.height) {
      const upperLeftCoords = mercatorMap.viewPointToCoords(0, 0);
      const bottomRightCoords = mercatorMap.viewPointToCoords(
        mapSize.width * Theme.pixelRatio(),
        mapSize.height * Theme.pixelRatio(),
      );

      const minRow = mercatorMap.tileRowForLatitude(upperLeftCoords[1]);
      const maxRow = mercatorMap.tileRowForLatitude(bottomRightCoords[1]);
      const minCol = mercatorMap.tileColumnForLongitude(upperLeftCoords[0]);
      const maxCol = mercatorMap.tileColumnForLongitude(bottomRightCoords[0]);

      const images = [];

      for (let row = minRow; row <= maxRow; row++) {
        for (let col = minCol; col <= maxCol; col++) {
          const imageRect = mercatorMap.viewRectForTile(col, row);
          const tileURL = replaceTemplateValues(uriTemplate, {
            x: col,
            y: row,
            z: Math.floor(mercatorMap.tileZoom),
          });

          loadingManager.beginLoading(tileURL);

          images.push(
            <Image
              key={tileURL}
              source={{ uri: tileURL }}
              style={[
                styles.imagePosition,
                {
                  left: imageRect.x / Theme.pixelRatio(),
                  top: imageRect.y / Theme.pixelRatio(),
                  width: imageRect.width / Theme.pixelRatio(),
                  height: imageRect.height / Theme.pixelRatio(),
                },
              ]}
              onError={() => {
                loadingManager.finishedLoading(tileURL);
              }}
              onLoad={() => {
                loadingManager.finishedLoading(tileURL);
              }}
            />,
          );
        }
      }

      content = images;
    }

    const onLayout = (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;

      setMapSize({ width, height });
    };

    return (
      <View ref={reference} style={[{ opacity: alpha }, styles.layerParent]} onLayout={onLayout}>
        {content}
      </View>
    );
  },
);

type MapTileLayerProps = {
  center: number[];
  zoom: number;
  uriTemplate: string;
  onTilesLoaded?: (value: number) => void;
  tileDimension?: number;
  alpha?: number;
  tileSizeMethod?: MapTileSizeMethod;
};

type MapSize = {
  width: number;
  height: number;
};
